import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cellKey, isCellCorrect, formatCellAnswer } from '../../../utils/tableAnswers';
const styles = `
/* InformationTransferTable.css */

.information-transfer-container {
  width: 100%;
  margin: 2rem 0;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.table-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.table-error {
  padding: 2rem;
  text-align: center;
  color: #d32f2f;
  background-color: #ffebee;
  border-radius: 4px;
}
.blank-input {
  min-width: 80px;
  padding: 0.5rem;
  border: none;
  border-bottom: 2px solid #333;
  text-align: center;
  font-weight: 500;
  font-size: 0.95rem;
  background: transparent;
}

.blank-input:focus {
  outline: none;
  border-bottom-color: #1976d2;
}
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.information-transfer-table {
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  font-size: 0.95rem;
  border: 2px solid #333;
}

/* Table Headers */
.information-transfer-table thead {
  background-color: #e8e8e8;
}

.information-transfer-table th {
  padding: 0.75rem;
  text-align: center;
  font-weight: 600;
  border: 1px solid #999;
  color: #333;
}

.row-label-header {
  width: 20%;
  min-width: 150px;
  background-color: #d0d0d0;
}

.column-header {
  width: 26.66%;
  vertical-align: middle;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.header-line {
  line-height: 1.3;
}

/* Table Body */
.information-transfer-table tbody tr {
  border-bottom: 1px solid #ddd;
}

.information-transfer-table tbody tr:hover {
  background-color: #f5f5f5;
}

.row-label {
  padding: 0.75rem;
  font-weight: 500;
  background-color: #f0f0f0;
  border: 1px solid #999;
  vertical-align: middle;
  text-align: left;
}

.label-line {
  line-height: 1.4;
}

.data-cell {
  padding: 0.75rem;
  border: 1px solid #ccc;
  vertical-align: middle;
  min-height: 60px;
}

/* Checkbox Group */
.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.checkbox-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: default;
}

.checkbox-item label {
  font-size: 0.9rem;
  cursor: default;
  user-select: none;
}

/* Radio Group */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.radio-item input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: default;
}

.radio-item label {
  font-size: 0.9rem;
  cursor: default;
  user-select: none;
}

/* Fill in the Blank */
.fill-blank {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
}

.blank-line {
  min-width: 80px;
  padding: 0.5rem;
  border-bottom: 2px solid #333;
  text-align: center;
  font-weight: 500;
}

.unit-label {
  font-size: 0.9rem;
  color: #666;
  white-space: nowrap;
}

/* Time Range */
.time-range {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
}

.time-from,
.time-to {
  font-size: 0.9rem;
  padding: 0.25rem 0;
}

/* Text Content */
.text-content {
  text-align: center;
  padding: 0.5rem;
  font-weight: 500;
}

/* Default Cell */
.default-cell {
  text-align: center;
  color: #999;
  font-style: italic;
}

/* Table Footnote */
.table-footnote {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  font-size: 0.85rem;
  color: #856404;
}

.table-footnote p {
  margin: 0;
}

/* Responsive Design */
@media screen and (max-width: 1024px) {
  .information-transfer-table {
    font-size: 0.85rem;
  }

  .data-cell {
    padding: 0.5rem;
  }

  .blank-line {
    min-width: 60px;
  }
}

@media screen and (max-width: 768px) {
  .information-transfer-container {
    padding: 0.5rem;
  }

  .information-transfer-table {
    font-size: 0.8rem;
  }

  .row-label-header {
    min-width: 100px;
  }

  .checkbox-item input[type="checkbox"],
  .radio-item input[type="radio"] {
    width: 16px;
    height: 16px;
  }

  .checkbox-item label,
  .radio-item label {
    font-size: 0.8rem;
  }
}

/* Print Styles */
@media print {
  .information-transfer-container {
    box-shadow: none;
    background-color: white;
  }

  .information-transfer-table {
    border: 2px solid black;
  }

  .information-transfer-table th,
  .information-transfer-table td {
    border: 1px solid black;
  }
}

/* Printed values, results */
.given-value {
  color: #374151;
  font-weight: 500;
}

.data-cell.is-correct {
  background-color: #ecfdf5;
  box-shadow: inset 0 0 0 2px #34d399;
}

.data-cell.is-wrong {
  background-color: #fff1f2;
  box-shadow: inset 0 0 0 2px #fda4af;
}

.gap-solution {
  margin-top: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #047857;
}

.time-input-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.time-input-row .blank-input {
  width: 4.5rem;
}
`;

/**
 * Dynamic Information Transfer Table Component for ÖSD B2 Listening Part 2
 *
 * This component renders a table for the information transfer task.
 * It reads all data from JSON and can handle any topic.
 *
 * @param {Object} tableData - The table configuration from JSON
 */
const InformationTransferTable = ({ tableData, questions = [], answers = {}, onAnswer, showResults = false }) => {
  const { t } = useTranslation();

  // One scored item per row; its cells are keyed "col1" or, in a time row, "col1|to".
  const rowItems = useMemo(() => {
    const map = {};
    questions.filter((q) => q.kind === 'row').forEach((q) => {
      map[q.row] = { question: q, cells: Object.fromEntries(q.cells.map((c) => [cellKey(c), c])) };
    });
    return map;
  }, [questions]);

  if (!tableData) {
    return (

      <div className="table-error">
        <p>Keine Tabellendaten verfügbar.</p>
      </div>
    );
  }

  const { title, columns, rows, footnote } = tableData;
  const editable = typeof onAnswer === 'function' && !showResults;

  const itemFor = (rowId, key) => {
    const item = rowItems[rowId];
    const cell = item?.cells[key];
    if (!cell) return null;
    const rowAnswer = answers[item.question.questionId] || {};
    return {
      cell,
      value: rowAnswer[key],
      set: (value) => editable && onAnswer(item.question.questionId, { ...rowAnswer, [key]: value }),
      label: `${item.question.statement} – ${columns.find((c) => c.id === cell.col)?.header.replace(/\n/g, ' ') || ''}`,
    };
  };

  const solutionNote = (item) => {
    if (!showResults || isCellCorrect(item.cell, item.value)) return null;
    return <div className="gap-solution">{t('infoTable.correctAnswer', { answer: formatCellAnswer(item.cell) })}</div>;
  };

  const textInput = (item) => (
    <input
      type="text"
      inputMode={item.cell.kind === 'number' || item.cell.kind === 'time' ? 'decimal' : 'text'}
      className="blank-input"
      placeholder="________"
      aria-label={item.label}
      value={item.value ?? ''}
      readOnly={!editable}
      onChange={(e) => item.set(e.target.value)}
    />
  );

  /** Every cell is a blank to fill in, like the printed ÖSD answer sheet. */
  const renderCellContent = (row, columnId) => {
    const cellData = row.answers?.[columnId];

    switch (row.type) {
      case 'checkbox': {
        const item = itemFor(row.id, columnId);
        const ticked = item ? (item.value || []) : [];
        const toggle = (option) => {
          if (!item) return;
          item.set(ticked.includes(option) ? ticked.filter((o) => o !== option) : [...ticked, option]);
        };
        return (
          <div>
            <div className="checkbox-group">
              {row.options.map((option, idx) => (
                <div key={idx} className="checkbox-item">
                  <input
                    type="checkbox"
                    id={`${row.id}-${columnId}-${idx}`}
                    checked={ticked.includes(option)}
                    disabled={!item || !editable}
                    onChange={() => toggle(option)}
                  />
                  <label htmlFor={`${row.id}-${columnId}-${idx}`}>{option}</label>
                </div>
              ))}
            </div>
            {item && solutionNote(item)}
          </div>
        );
      }

      case 'fill-blank': {
        const item = itemFor(row.id, columnId);
        if (!item) return <div className="default-cell">–</div>;
        return (
          <div className="fill-blank">
            <div className="time-input-row">
              {textInput(item)}
              {row.unit && <span className="unit-label">{row.unit}</span>}
            </div>
            {solutionNote(item)}
          </div>
        );
      }

      case 'fill-blank-time': {
        // { from, to }: the start time is printed, the end time is the blank.
        // { suffix }: a single blank followed by text, e.g. "___ Stunden geöffnet".
        const to = itemFor(row.id, `${columnId}|to`);
        const single = itemFor(row.id, columnId);
        if (single) {
          return (
            <div className="time-range">
              <div className="time-input-row">{textInput(single)}<span>{cellData?.suffix}</span></div>
              {solutionNote(single)}
            </div>
          );
        }
        return (
          <div className="time-range">
            <div className="time-input-row"><span>von</span><span className="given-value">{cellData?.from}:00</span><span>Uhr</span></div>
            <div className="time-input-row">
              <span>bis</span>
              {to ? textInput(to) : <span className="given-value">{cellData?.to}</span>}
              <span>Uhr</span>
            </div>
            {to && solutionNote(to)}
          </div>
        );
      }

      case 'text':
        return (
          <div className="text-content">
            {cellData || '________'}
          </div>
        );

      default:
        return <div className="default-cell">{cellData || '–'}</div>;
    }
  };

  const cellClass = (row, columnId) => {
    if (!showResults) return 'data-cell';
    const item = itemFor(row.id, columnId) || itemFor(row.id, `${columnId}|to`);
    if (!item) return 'data-cell';
    return `data-cell${isCellCorrect(item.cell, item.value) ? ' is-correct' : ' is-wrong'}`;
  };

  return (
      <>
      <style>{styles}</style>
    <div className="information-transfer-container">
      <div className="table-header">
        <h3>{title}</h3>
      </div>

      <div className="table-responsive">
        <table className="information-transfer-table">
          <thead>
            <tr>
              <th className="row-label-header">Kategorie</th>
              {columns.map((col) => (
                <th key={col.id} className="column-header">
                  <div className="header-content">
                    {col.header.split('\n').map((line, idx) => (
                      <div key={idx} className="header-line">
                        {line}
                      </div>
                    ))}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className={`row-type-${row.type}`}>
                <td className="row-label">
                  {row.label.split('\n').map((line, idx) => (
                    <div key={idx} className="label-line">
                      {line}
                    </div>
                  ))}
                </td>
                {columns.map((col) => (
                  <td key={col.id} className={cellClass(row, col.id)}>
                    {renderCellContent(row, col.id)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {footnote && (
        <div className="table-footnote">
          <p>{footnote}</p>
        </div>
      )}
     </div>
    </>
  );
};

export default InformationTransferTable;